import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {Box, Card, CardContent, Grid, Skeleton, Stack, Typography} from '@mui/material';
import {is_mobile_device} from 'common';
import CancelButton from 'components/CancelButton';
import DoubleBarPriceGraph from 'components/DoubleBarPriceGraph';
import PriceTag from 'components/PriceTag';
import RemoveButton from 'components/RemoveButton';
import TakeButton from 'components/TakeButton';
import {ReactNode, useEffect, useState} from 'react';
import {IOrder} from 'types';

function OuterBox(props: {children: ReactNode}) {
	return (
		<Box width="100%" minHeight="75px" px={1} display="flex" alignItems="center" justifyContent="space-around" bgcolor="#181818" borderRadius="5px" boxSizing="border-box">
			{props.children}
		</Box>
	);
}

export default function OrderState(props: {index: number; order: IOrder}) {
	const {index, order} = props;
	const {current_price, close_price, ticker, order_price, loss_cut_price, target_price, state, volume} = order;

	const is_mobile = is_mobile_device();
	const [is_processing, set_is_processing] = useState(false);

	const position: 'long' | 'short' = target_price > loss_cut_price ? 'long' : 'short';
	const is_disabled = state === 'open_watch' || state === 'canceled' || state === 'pending';
	const [is_cancel_enabled, set_is_cancel_enabled] = useState(false);

	const fee_ratio = order.ask_fee + order.bid_fee;

	const target_fee_value = fee_ratio * target_price * volume;
	const take_profit_value = (position === 'long' ? target_price - order_price : order_price - target_price) * volume - target_fee_value;

	const loss_cut_fee_value = fee_ratio * loss_cut_price * volume;
	const loss_cut_value = (position === 'long' ? loss_cut_price - order_price : order_price - loss_cut_price) * volume - loss_cut_fee_value;

	const graph_width = '180px';

	useEffect(() => {
		let timeoutId: NodeJS.Timeout | null = null;
		const shouldEnableCancel = !is_processing && !is_cancel_enabled && (state === 'opening' || state === 'closing');
		// Enable cancellation after a configurable delay (default: 10 seconds)
		const CANCEL_DELAY_MS = 10000; // Adjust this value as per business requirements
		if (shouldEnableCancel) timeoutId = setTimeout(() => set_is_cancel_enabled(true), CANCEL_DELAY_MS);

		return () => {
			if (timeoutId) clearTimeout(timeoutId);
		};
	}, [state, is_processing, is_cancel_enabled]);

	useEffect(() => {
		if (state === 'canceled' || state === 'open' || state === 'closed') set_is_cancel_enabled(false);
	}, [state]);

	return (
		<OuterBox>
			{!is_cancel_enabled && <RemoveButton index={index} order={order} is_processing={is_processing} set_is_processing={set_is_processing} />}
			{is_cancel_enabled && <CancelButton index={index} order={order} is_processing={is_processing} set_is_processing={set_is_processing} />}

			<Grid container display="flex" alignItems="center" justifyContent="space-around" maxWidth="450px" direction={{xs: 'column', md: 'row'}}>
				{is_mobile ? (
					<Grid item width="100%" display="flex" flexDirection="row" justifyContent={'space-between'}>
						<PriceTag price={loss_cut_price} color="error" sign_disabled />
						<PriceTag price={take_profit_value} color="primary.main" />
					</Grid>
				) : (
					<Grid item width="auto">
						<PriceTag price={loss_cut_price} color="error" sign_disabled />
						<PriceTag price={loss_cut_value} color="error" />
					</Grid>
				)}

				<Grid item>
					<DoubleBarPriceGraph
						width={graph_width}
						tag={ticker}
						loss_cut_price={loss_cut_price}
						target_price={target_price}
						order_price={order_price}
						current_price={close_price ?? current_price}
						disabled={is_disabled}
						heat={order.heat}
						bgcolor={position === 'long' ? '#007c00ff' : '#a70303ff'}
					/>
				</Grid>

				{is_mobile ? (
					<Grid item width="100%" display="flex" flexDirection="row" justifyContent={'space-between'}>
						<PriceTag price={loss_cut_value} color="error" />
						<PriceTag price={target_price} color="primary.main" sign_disabled />
					</Grid>
				) : (
					<Grid item width="auto" textAlign={'right'}>
						<PriceTag price={take_profit_value} color="primary.main" />
						<PriceTag price={target_price} color="primary.main" sign_disabled />
					</Grid>
				)}
			</Grid>

			<TakeButton order={order} is_processing={is_processing} set_is_processing={set_is_processing} />
		</OuterBox>
	);
}

export function OrderStateLoading() {
	return (
		<OuterBox>
			<Skeleton height="75px" animation="wave" />
		</OuterBox>
	);
}

export function OrderStateEmpty() {
	return (
		<OuterBox>
			<Stack alignItems="center" justifyContent="center" minHeight="75px">
				<WarningAmberOutlinedIcon fontSize="large" />
				<Typography variant="body1">No orders</Typography>
			</Stack>
		</OuterBox>
	);
}
